/**
 * Serializes Maps to arrays/objects for JSON compatibility.
 * Used when saving configs or creating cache keys.
 *
 * @param {Object} config - Config object potentially containing Maps
 * @returns {Object} - Config with Maps converted to serializable formats
 */
export const serializeMaps = (config) => {
    if (!config) return config

    const serialized = { ...config }

    if (config.seriesTypeMap instanceof Map) {
        serialized.seriesTypeMap = Array.from(config.seriesTypeMap.entries())
    }

    if (config.selectedDates instanceof Map) {
        serialized.selectedDates = Object.fromEntries(config.selectedDates)
    }

    return serialized
}

/**
 * Deserializes arrays/objects back to Maps.
 * Used when loading saved configs.
 *
 * @param {Object} config - Config object with serialized Maps
 * @returns {Object} - Config with Maps restored
 */
export const deserializeMaps = (config) => {
    if (!config) return config

    const deserialized = { ...config }

    // Handle seriesTypeMap
    if (Array.isArray(config.seriesTypeMap)) {
        deserialized.seriesTypeMap = new Map(config.seriesTypeMap)
    } else if (config.seriesTypeMap && !(config.seriesTypeMap instanceof Map)) {
        // Handle case where it's a plain object
        deserialized.seriesTypeMap = new Map(Object.entries(config.seriesTypeMap))
    } else if (!config.seriesTypeMap) {
        deserialized.seriesTypeMap = new Map()
    }

    // Handle selectedDates
    if (config.selectedDates && !(config.selectedDates instanceof Map)) {
        if (Array.isArray(config.selectedDates)) {
            deserialized.selectedDates = new Map(config.selectedDates)
        } else {
            deserialized.selectedDates = new Map(Object.entries(config.selectedDates))
        }
    } else if (!config.selectedDates) {
        deserialized.selectedDates = new Map()
    }

    return deserialized
}

/**
 * Creates a stable cache key from data, query config, and view type.
 * Only includes properties that affect the API response.
 * Style/rendering properties in viewConfig are excluded.
 *
 * @param {Object} dataConfig - Data configuration
 * @param {Object} queryConfig - Query configuration
 * @param {string} view - View type ('chart', 'table', 'kpi') - affects API action
 * @returns {Array} - React Query cache key
 */
export const createCacheKey = (dataConfig, queryConfig, view) => {
    if (!dataConfig || !queryConfig) {
        return ['summaryData', null]
    }

    const keyObject = {
        // View type (affects which API action is used)
        view: view || 'chart',

        // Data config (what to fetch)
        selectedItems: dataConfig.selectedItems?.map((item) => ({
            // Core identity
            measureName: item.measureName,
            reportSeriesTableID: item.reportSeriesTableID,
            worksheetID: item.worksheetID,
            metricName: item.metricName,
            metricKey: item.metricKey,
            kpi: item.kpi,
            columnName: item.columnName,
            rangeName: item.rangeName,
            // Aggregation settings
            aggRule: item.aggRule,
            aggregation: item.aggregation,
            period: item.period,
            calendarType: item.calendarType,
            cumulative: item.cumulative,
            // Period/time shift settings
            shiftPeriod: item.shiftPeriod,
            numPeriodsShifted: item.numPeriodsShifted,
            metricType: item.metricType,
            // Filter settings
            filter: item.filter,
            savedFilterID: item.savedFilterID,
            // Format (affects display but included for completeness)
            format: item.format,
        })),
        categoryAxis: dataConfig.categoryAxis,
        drillAttribute: dataConfig.drillAttribute,
        filter: dataConfig.filter,
        savedFilter: dataConfig.savedFilter,
        worksheetID: dataConfig.worksheetID,
        rangeName: dataConfig.rangeName,

        // Query config (how to aggregate)
        period: queryConfig.period,
        timeRange: queryConfig.timeRange,
        calendarType: queryConfig.calendarType,
        from: queryConfig.from,
        to: queryConfig.to,
        sortColumns: queryConfig.sortColumns,
        displayRows: queryConfig.displayRows,
        tableTimeSeries: queryConfig.tableTimeSeries,
        disableTimeSeries: queryConfig.disableTimeSeries,
    }

    return ['summaryData', JSON.stringify(keyObject)]
}

/**
 * Compares two cache keys to determine if they represent the same query.
 * Useful for debugging cache behavior.
 *
 * @param {Array} key1 - First cache key
 * @param {Array} key2 - Second cache key
 * @returns {boolean} - Whether the keys are equal
 */
export const areCacheKeysEqual = (key1, key2) => {
    if (!key1 || !key2) return false
    if (key1.length !== key2.length) return false
    return key1.every((part, index) => part === key2[index])
}
